/**
 * Gameplan editor body — rendered by GameplanPanel whenever
 * activeGameplanId is non-null.
 *
 * Layout is intentionally compact so the multi-line canvas gets the
 * lion's share of vertical space:
 *
 *   - Top bar: ← all gameplans (left)  · delete gameplan (right)
 *   - Title + description on one row (title left, description right)
 *   - LineCarousel (tab strip + active-line action bar)
 *   - LinesCanvas — flex: 1, fills the rest
 *
 * The old "LINES · N" label and "gameplans · N" label are gone; the
 * counts are already visible inline (carousel pills, breadcrumb).
 */
import { useEffect, useState } from 'react';
import { useAppStore } from '../state/store';
import type {
  Counter,
  MediaRefsIndex,
  Move,
  Position,
} from '../types/domain';
import type { Gameplan } from '../types/gameplan';
import { downloadGameplanCheatsheet, downloadLinePng } from './gameplan-export';
import { LineCarousel } from './LineCarousel';
import { LinesCanvas } from './LinesCanvas';
import { BLUE, INK3, LINE2, RADIUS, hexA } from './tokens';

type Props = {
  gameplan: Gameplan;
  positions: Position[];
  moves: Move[];
  counters: Counter[];
  mediaRefs: MediaRefsIndex | null;
};

export function GameplanEditor({
  gameplan,
  positions,
  moves,
  counters,
  mediaRefs,
}: Props) {
  const renameGameplan = useAppStore((s) => s.renameGameplan);
  const setGameplanDescription = useAppStore((s) => s.setGameplanDescription);
  // setActiveGameplan is no longer needed here — universal back in
  // ViewSwitcher steps out of an active gameplan into the index.
  const deleteGameplan = useAppStore((s) => s.deleteGameplan);
  const activeLineId = useAppStore((s) => s.activeLineId);
  const setActiveLine = useAppStore((s) => s.setActiveLine);
  const createLine = useAppStore((s) => s.createLine);
  const gameplanFilter = useAppStore((s) => s.gameplanFilter);
  const setGameplanFilter = useAppStore((s) => s.setGameplanFilter);
  const startTutorial = useAppStore((s) => s.startTutorial);

  // Title edit state — local draft so partial typing doesn't write
  // through to localStorage on every keystroke.
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(gameplan.name);
  useEffect(() => setTitleDraft(gameplan.name), [gameplan.name]);

  // Description draft — committed on blur / Cmd+Enter.
  const [descDraft, setDescDraft] = useState(gameplan.description ?? '');
  useEffect(() => setDescDraft(gameplan.description ?? ''), [
    gameplan.description,
  ]);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // Auto-pick the first line if there's no active selection but the
  // plan has lines. Happens after deleting the active line, or on the
  // first entry when the store has lines from a prior session.
  useEffect(() => {
    if (activeLineId === null && gameplan.lines.length > 0) {
      setActiveLine(gameplan.lines[0].id);
    }
  }, [activeLineId, gameplan.lines, setActiveLine]);

  const commitTitle = () => {
    const trimmed = titleDraft.trim();
    if (trimmed && trimmed !== gameplan.name) {
      renameGameplan(gameplan.id, trimmed);
    } else if (!trimmed) {
      setTitleDraft(gameplan.name);
    }
    setEditingTitle(false);
  };
  const commitDesc = () => {
    if (descDraft !== (gameplan.description ?? '')) {
      setGameplanDescription(gameplan.id, descDraft);
    }
  };

  const noLines = gameplan.lines.length === 0;

  // Active line lookup reused by the actions menu (PNG export needs a
  // non-empty line to rasterize).
  const activeLineForExport =
    activeLineId !== null
      ? gameplan.lines.find((l) => l.id === activeLineId) ?? null
      : null;
  const activeLineHasNodes =
    !!activeLineForExport && activeLineForExport.nodes.length > 0;

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      {/* Old top bar removed — universal back (in the ViewSwitcher row
          above) steps out of a gameplan now, and the eye / ⋯ actions
          moved into the title row's right-side vertical column (see
          the body section below). This zero-height wrapper preserves
          the JSX boundaries so the deletion is a small change. */}
      <div style={{ display: 'none' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {/* Filter-to-gameplan eyeball. On = the 3D graph shows only
              this gameplan (and glows the active line within it). */}
          <button
            onClick={() => setGameplanFilter(!gameplanFilter)}
            aria-pressed={gameplanFilter}
            aria-label={
              gameplanFilter
                ? 'Showing only this gameplan in the graph'
                : 'Filter the graph to only this gameplan'
            }
            title={
              gameplanFilter
                ? 'Showing only this gameplan — click to show the full map'
                : 'Filter the graph to only this gameplan'
            }
            style={{
              ...topBtn,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px 7px',
              border: gameplanFilter
                ? '1px solid rgba(155,231,255,0.55)'
                : '1px solid rgba(255,255,255,0.10)',
              background: gameplanFilter
                ? 'rgba(155,231,255,0.14)'
                : 'transparent',
              color: gameplanFilter ? '#cdf6ff' : '#9aa3af',
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
          {/* Actions menu — line PNG, cheatsheet, and delete collapsed
              into a single dropdown to keep the header uncluttered. */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Gameplan actions"
              title="Gameplan actions"
              style={{
                ...topBtn,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px 9px',
                lineHeight: 1,
                fontSize: 14,
              }}
            >
              ⋯
            </button>
            {menuOpen ? (
              <>
                {/* Click-away backdrop. */}
                <div
                  onClick={() => {
                    setMenuOpen(false);
                    setConfirmingDelete(false);
                  }}
                  style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                />
                <div
                  role="menu"
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 4px)',
                    right: 0,
                    zIndex: 41,
                    minWidth: 176,
                    background: 'rgba(20,22,28,0.98)',
                    border: '1px solid rgba(255,255,255,0.10)',
                    borderRadius: 8,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                    padding: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <button
                    role="menuitem"
                    disabled={!activeLineHasNodes}
                    onClick={async () => {
                      setMenuOpen(false);
                      if (
                        !activeLineForExport ||
                        activeLineForExport.nodes.length === 0
                      ) {
                        return;
                      }
                      try {
                        await downloadLinePng(
                          activeLineForExport,
                          gameplan.name,
                          positions,
                          moves,
                          counters,
                        );
                      } catch (err) {
                        console.error('PNG export failed:', err);
                      }
                    }}
                    title={
                      activeLineHasNodes
                        ? 'Download the active line as a PNG image'
                        : 'Select a non-empty line first'
                    }
                    style={{
                      ...menuItem,
                      color: activeLineHasNodes ? '#cdf6ff' : '#6b7280',
                      cursor: activeLineHasNodes ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Download line PNG
                  </button>
                  <button
                    role="menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      downloadGameplanCheatsheet(
                        gameplan,
                        positions,
                        moves,
                        counters,
                        mediaRefs,
                      );
                    }}
                    title="Download a printable HTML cheatsheet of this gameplan"
                    style={{ ...menuItem, color: '#cdf6ff' }}
                  >
                    Download cheatsheet
                  </button>
                  <div
                    style={{
                      height: 1,
                      background: 'rgba(255,255,255,0.07)',
                      margin: '2px 4px',
                    }}
                  />
                  {confirmingDelete ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        role="menuitem"
                        onClick={() => {
                          deleteGameplan(gameplan.id);
                          setConfirmingDelete(false);
                          setMenuOpen(false);
                        }}
                        style={{
                          ...menuItem,
                          flex: 1,
                          textAlign: 'center',
                          background: 'rgba(255,128,140,0.18)',
                          border: '1px solid rgba(255,128,140,0.40)',
                          color: '#ffc0c8',
                          fontWeight: 600,
                        }}
                      >
                        confirm
                      </button>
                      <button
                        role="menuitem"
                        onClick={() => setConfirmingDelete(false)}
                        style={{ ...menuItem, flex: 1, textAlign: 'center' }}
                      >
                        cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      role="menuitem"
                      onClick={() => setConfirmingDelete(true)}
                      style={{ ...menuItem, color: '#ffaab2' }}
                    >
                      Delete gameplan
                    </button>
                  )}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* Body — flex column. Canvas wrapper at the bottom gets flex:1
          so it absorbs all leftover vertical space. */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          padding: '10px 14px 12px',
          gap: 10,
        }}
      >
        {/* Title + description side-by-side. Title is editable in
            place; description is a compact textarea on the right that
            users can drag-grow if they want more room. */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'stretch',
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            {editingTitle ? (
              <input
                autoFocus
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    commitTitle();
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setTitleDraft(gameplan.name);
                    setEditingTitle(false);
                  }
                  e.stopPropagation();
                }}
                style={{
                  width: '100%',
                  background: 'rgba(0,0,0,0.4)',
                  border: '1px solid rgba(155,231,255,0.30)',
                  borderRadius: 4,
                  color: '#e6e8ef',
                  fontSize: 16,
                  fontWeight: 600,
                  padding: '4px 6px',
                  outline: 'none',
                }}
              />
            ) : (
              <div
                title="Click to rename"
                onClick={() => setEditingTitle(true)}
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: '#e6e8ef',
                  cursor: 'text',
                  padding: '2px 2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {gameplan.name}
              </div>
            )}
          </div>

          <textarea
            value={descDraft}
            onChange={(e) => setDescDraft(e.target.value)}
            onBlur={commitDesc}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                commitDesc();
                (e.currentTarget as HTMLTextAreaElement).blur();
              }
            }}
            placeholder="Description (optional)…"
            rows={2}
            style={{
              flex: 1.4,
              minWidth: 0,
              background: 'rgba(0,0,0,0.25)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 4,
              color: '#cdd2da',
              fontSize: 11,
              lineHeight: 1.45,
              padding: '6px 8px',
              outline: 'none',
              // Disable manual resize and cap to roughly 2 visible
              // lines. Single-line entries collapse to look like an
              // input; two-line entries stay fully visible; anything
              // longer scrolls internally so the editor header
              // doesn't keep pushing the canvas down.
              resize: 'none',
              fontFamily: 'inherit',
              minHeight: 30,
              maxHeight: 'calc(2 * 1.45em + 14px)',
              overflowY: 'auto',
            }}
          />

          {/* Vertical actions column — ⋯ menu on top, filter-eye on
              the bottom. Replaces the old top bar so the editor's
              first row holds title + description + actions in one
              compact strip instead of stacking two strips. The two
              buttons here drive the same gameplanFilter and menuOpen
              state as the (now-removed) top-bar versions did. */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              flexShrink: 0,
              alignItems: 'flex-end',
            }}
          >
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Gameplan actions"
                title="Gameplan actions"
                style={{
                  ...topBtn,
                  width: 30,
                  height: 24,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  lineHeight: 1,
                  fontSize: 14,
                }}
              >
                ⋯
              </button>
              {menuOpen && (
                <>
                  <div
                    onClick={() => {
                      setMenuOpen(false);
                      setConfirmingDelete(false);
                    }}
                    style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                  />
                  <div
                    role="menu"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      right: 0,
                      zIndex: 41,
                      minWidth: 184,
                      background: 'rgba(20,22,28,0.98)',
                      border: '1px solid rgba(255,255,255,0.10)',
                      borderRadius: 8,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                      padding: 4,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                    }}
                  >
                    <button
                      role="menuitem"
                      disabled={!activeLineHasNodes}
                      onClick={async () => {
                        setMenuOpen(false);
                        if (
                          !activeLineForExport ||
                          activeLineForExport.nodes.length === 0
                        ) {
                          return;
                        }
                        try {
                          await downloadLinePng(
                            activeLineForExport,
                            gameplan.name,
                            positions,
                            moves,
                            counters,
                          );
                        } catch (err) {
                          console.error('PNG export failed:', err);
                        }
                      }}
                      style={{
                        ...menuItem,
                        color: activeLineHasNodes ? '#cdf6ff' : '#6b7280',
                        cursor: activeLineHasNodes ? 'pointer' : 'not-allowed',
                      }}
                    >
                      Download line PNG
                    </button>
                    <button
                      role="menuitem"
                      onClick={() => {
                        setMenuOpen(false);
                        downloadGameplanCheatsheet(
                          gameplan,
                          positions,
                          moves,
                          counters,
                          mediaRefs,
                        );
                      }}
                      style={{ ...menuItem, color: '#cdf6ff' }}
                    >
                      Download cheatsheet
                    </button>
                    <div
                      style={{
                        height: 1,
                        background: 'rgba(255,255,255,0.07)',
                        margin: '2px 4px',
                      }}
                    />
                    {confirmingDelete ? (
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          role="menuitem"
                          onClick={() => {
                            deleteGameplan(gameplan.id);
                            setConfirmingDelete(false);
                            setMenuOpen(false);
                          }}
                          style={{ ...menuItem, color: '#ffaab2' }}
                        >
                          Confirm
                        </button>
                        <button
                          role="menuitem"
                          onClick={() => setConfirmingDelete(false)}
                          style={menuItem}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        role="menuitem"
                        onClick={() => setConfirmingDelete(true)}
                        style={{ ...menuItem, color: '#ffaab2' }}
                      >
                        Delete gameplan
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>

            <button
              onClick={() => setGameplanFilter(!gameplanFilter)}
              aria-pressed={gameplanFilter}
              aria-label={
                gameplanFilter
                  ? 'Showing only this gameplan in the graph'
                  : 'Filter the graph to only this gameplan'
              }
              title={
                gameplanFilter
                  ? 'Showing only this gameplan — click to show the full map'
                  : 'Filter the graph to only this gameplan'
              }
              style={{
                ...topBtn,
                width: 30,
                height: 24,
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: gameplanFilter
                  ? '1px solid rgba(155,231,255,0.55)'
                  : '1px solid rgba(255,255,255,0.10)',
                background: gameplanFilter
                  ? 'rgba(155,231,255,0.14)'
                  : 'transparent',
                color: gameplanFilter ? '#cdf6ff' : '#9aa3af',
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          </div>
        </div>

        {noLines ? (
          <div
            style={{
              fontSize: 12,
              opacity: 0.5,
              lineHeight: 1.55,
              padding: '8px 4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 10,
            }}
          >
            <span>
              No lines yet. A line is a top-to-bottom sequence of moves
              and positions.
            </span>
            <button
              onClick={() => {
                const id = createLine(gameplan.id, 'Line 1');
                setActiveLine(id);
              }}
              style={{
                background: 'rgba(155,231,255,0.10)',
                color: '#cdf6ff',
                border: '1px solid rgba(155,231,255,0.30)',
                borderRadius: 4,
                padding: '4px 12px',
                fontSize: 11,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + new line
            </button>
          </div>
        ) : (
          <>
            {/* Play launcher stays FROZEN on the left while the line
                pills scroll horizontally beside it. Plays the active
                line as a guided step-through on the 3D map. */}
            {(() => {
              const activeLine = gameplan.lines.find(
                (l) => l.id === activeLineId,
              );
              const canPlay = !!activeLine && activeLine.nodes.length > 0;
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    minWidth: 0,
                  }}
                >
                  <button
                    onClick={() => {
                      if (canPlay && activeLineId)
                        startTutorial(gameplan.id, activeLineId);
                    }}
                    disabled={!canPlay}
                    aria-label="Play line"
                    title={
                      canPlay
                        ? 'Walk this line node-by-node on the map'
                        : 'Add nodes to this line first'
                    }
                    style={{
                      flexShrink: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 30,
                      height: 30,
                      borderRadius: RADIUS.pill,
                      border: `1px solid ${canPlay ? hexA(BLUE, 0.4) : LINE2}`,
                      background: canPlay ? hexA(BLUE, 0.14) : 'transparent',
                      color: canPlay ? BLUE : INK3,
                      cursor: canPlay ? 'pointer' : 'not-allowed',
                      padding: 0,
                    }}
                  >
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      aria-hidden
                      style={{ marginLeft: 1 }}
                    >
                      <path d="M7 4.5v15l13-7.5z" fill="currentColor" />
                    </svg>
                  </button>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <LineCarousel
                      gameplan={gameplan}
                      activeLineId={activeLineId}
                    />
                  </div>
                </div>
              );
            })()}
            <div
              style={{
                flex: 1,
                // Low floor (was 280) so the canvas actually GIVES UP
                // depth when the drawer / sheet is short, instead of
                // pinning at 280 and forcing overflow that would
                // squeeze the (now flexShrink:0) carousel. The canvas
                // pans / scrolls internally, so a shallow canvas is
                // still usable; a clipped pill is not. 140px keeps a
                // couple of node rows visible at the tightest sizes.
                minHeight: 140,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <LinesCanvas
                gameplan={gameplan}
                activeLineId={activeLineId}
                positions={positions}
                moves={moves}
                counters={counters}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const topBtn: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.10)',
  color: '#cdd2da',
  borderRadius: 4,
  padding: '3px 10px',
  fontSize: 11,
  cursor: 'pointer',
};

const menuItem: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid transparent',
  borderRadius: 5,
  padding: '6px 10px',
  fontSize: 12,
  color: '#cdd2da',
  cursor: 'pointer',
  textAlign: 'left',
  width: '100%',
};
